import dayjs from "dayjs";
import {
  get,
  isNull,
  isUndefined,
  isNil as isNill,
  isEmpty,
  indexOf,
  isArray,
  isEqual,
} from "lodash";
import { Dispatch } from "redux";
import utc from "dayjs/plugin/utc";
import { UseFormSetError } from "react-hook-form";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { SurveyPDF } from "survey-pdf";
import { act } from "react";
import React, { LazyExoticComponent, ComponentType } from 'react';

import SnackbarUtils from "./SnackbarProvider";
import {
  IS_LOCALHOST,
  REACT_APP_ENDPOINT_ENV,
  configUrlForBlob,
} from "../constants/Config";
import { CloudEnvironmentConfig } from "../constants/AppConfigs";
import Logger from "./logHandlers/Logger";
import {
  AuthType,
  PatientUrgencyStatus,
  ReferralResponseMessages,
  ServiceStatus,
  UrgencyStatus,
} from "../constants/AllPatientRecord";
import { FormattedNumber } from "./PhoneNumber";
import { extractFields } from "../constants/PatientList";
import { AUTHDESC, AUTH_ID, DEFAULT_PAGE, PER_PAGE } from "../constants/FunctionalUtils";
import { DocumentType } from "../constants/Document";
import {
  Gender,
  PriorAuthMasterApiKey,
  LANGUAGE_KEYWORDS,
  PatientLanguages,
  URGENCY_STATUS_EXPEDITED_ID,
  requiredFieldMsg,
  DataPresentInLocalStorage,
} from "../constants/Constants";
import {
  alphabetSpaceAndHyphen,
  mbiRegex,
  onlyNumAlphabetRegex,
} from "../constants/Regex";
import {
  BenefitPlanGroup,
  PatientRequest,
  ReAuthReferralRequest,
  ReceivedMethods,
} from "../models/Patient";
import { SearchFilterModel } from "../models/Filter";
import {
  DropdownOption,
  DropdownOptionKeys,
} from "../components/formComponents/constant/FormComponents";
import { ExpeditedOptionColor } from "../styles/common/style";
import { colors, others } from "../styles/colors";
import { LofColumn, NotesJson } from "../models/Notes";
import { ReferralDiscipline } from "../models/Disciplines";
import { stateNameAbbreviations } from "../constants/StateAbbreviations";
import { NppesStateList } from "../models/PhysicianDetails";
import { PatientInformationViewResponse } from "../models/Api/PatientInformation";
import { BenefitPlanDetails } from "../models/Master";
import { ApiKeyNames, masterSyncPayload } from "../constants/MasterCache";
import { BooleanValues } from "constants/User";
import localConfig from "../configs/localConfig.json";

//Extend utc to dayjs
dayjs.extend(utc);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Param value is mandatory
export const isNil = (value: any) => {
  if (value == undefined) {
    return true;
  }
  return false;
};
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Param value is mandatory
export const length = (value: any) => {
  if (value !== undefined && value !== null && value.length > 0) {
    return true;
  }
  return false;
};
/**
 * @returns true if the value is an array and has length greater than 0
 */
export const checkLength = (value: any): boolean =>
  value && Array.isArray(value) && value.length > 0;

// tslint:disable-next-line:no-empty
export function neverReached() {
  return null;
}

//below function used for covert byte array to image
export const avator = (byteArray: string) => {
  if (!isNil(byteArray) && byteArray !== "/broken-image.jpg") {
    return `data:image/png;base64,${byteArray}`;
  }
  return "/broken-image.jpg";
};

//below function used for apply css for tabs
export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
    "data-testid": `myPatient-tabpanel-${index}`,
  };
};

//below function used for sending response to api call
export const get404InitialData = (response: any, initialData: any) => {
  return isNull(response) || isEmpty(response) ? initialData : response;
};

//below function used for sending error response to api call
export const ErrorResponse = (response: any) => {
  const msg = "Something went wrong!";
  switch (response.status) {
    case 422:
    case 400:
      return response;
    case 403:
      return response;
    case 404:
      return response;
    default:
      SnackbarUtils.warning(msg);
      break;
  }
};

// function to get user image by blobid
export const ProfileImage = (blobId: number) => {
  return `${configUrlForBlob}api/ProfileImages/GetBlobById/${blobId}`;
};

//function to get the value at path of object
export const getValue = (
  inputObj: any,
  nestedProp: string,
  defaultValue?: any
) => {
  if (isUndefined(defaultValue)) {
    defaultValue = null;
  }
  return get(inputObj, nestedProp, defaultValue);
};
/**
 * Method to execute all init calls post config Load
 *
 */
export const initAfterConfigLoad = (): void => {
  Logger.suppressConsole();
};

/**
 * Method to get the json config file name for development and non development environmnet. Default is config.json
 * For development environment it will return config.json file based on REACT_APP_ENDPOINT_ENV value
 *
 * @returns {string} This is the json config file name to get from the pipeline or local path
 */
export const GetConfigFileName = (): string => {
  let fileName = "/config";

  if (IS_LOCALHOST) {
    const selectedEndpointEnv: string = REACT_APP_ENDPOINT_ENV;

    switch (selectedEndpointEnv) {
      case CloudEnvironmentConfig.SB1:
        fileName = `${fileName}-sb1`;
        break;
      case CloudEnvironmentConfig.QA1:
        fileName = `${fileName}-qa1`;
        break;
      case CloudEnvironmentConfig.DEV1:
        fileName = `${fileName}-dev1`;
        break;
      case CloudEnvironmentConfig.STG:
        fileName = `${fileName}-stg1`;
        break;
      case CloudEnvironmentConfig.SB2:
        fileName = `${fileName}-sb2`;
        break;
      case CloudEnvironmentConfig.DEV2:
        fileName = `${fileName}-dev2`;
        break;
      case CloudEnvironmentConfig.QA2:
        fileName = `${fileName}-qa2`;
        break;
    }
  }

  fileName = `${fileName}.json`;

  return fileName;
};

//Function to set scroll at top
export const scrollTop = () => {
  return window.scrollTo(0, 0);
};

export const smoothScroll = (currentOffsetTop: number) => {
  window.scrollTo({
    top: currentOffsetTop,
    behavior: "smooth",
  });
};

/**
 * @param {Dispatch}  dispatch -redux dispatch
 * @param {any} response - api response
 * @param {any} errorDispatch - dispatch type to set error response
 * @param {any} successDispatch - dispatch type to set success response
 * @param {any} successMessage - message to display success response
 * @param {any} errorMessage - message to display error response
 */
export const DispatchResponse = (
  dispatch: Dispatch,
  response: any,
  errorDispatch: any,
  successDispatch: any,
  successMessage?: any,
  errorMessage?: any
) => {
  switch (response.status) {
    case 422:
    case 400:
      dispatch({
        response,
        type: errorDispatch,
      });
      if (errorMessage) {
        SnackbarUtils.error(ReferralResponseMessages(errorMessage));
      }
      break;
    case 200:
    case 201:
      dispatch({
        response: response.data,
        type: successDispatch,
      });
      if (successMessage) {
        SnackbarUtils.success(ReferralResponseMessages(successMessage));
      }
      break;
    default:
      break;
  }
};

/**
 *
 * @param payload api payload
 * @returns paylaod in query string format
 */
export const GetQueryString = (payload: any) => {
  const QueryString = Object.keys(payload)
    .map((key) =>
      payload[key] !== null || undefined ? key + "=" + payload[key] : ""
    )
    .filter((str) => str !== "") // Filter out empty strings
    .join("&");

  return QueryString;
};
/**
 *
 * @param phoneNumber string formatted/not formatted
 * @returns formatted phone number
 */

type NullableString = string | null | undefined;

export const getFormattedPhoneNo = (phoneNumber: NullableString) => {
  if (isNull(phoneNumber) || isUndefined(phoneNumber)) {
    return phoneNumber;
  } else {
    return FormattedNumber(phoneNumber.replace(/\D/g, ""), phoneNumber.length);
  }
};

/**
 *
 * @param referralId string referralId
 * @returns return string referral id if it includes ?
 */
export const getReferralId = (referralId: string) => {
  if (referralId.includes("?")) {
    return referralId.split("?")[0];
  }
  return referralId;
};

export const getServiceRequestNumber = (serviceRequestNumber: string) => {
  let serviceNumber = "";
  if (!isNill(serviceRequestNumber)) {
    serviceNumber = getValue(serviceRequestNumber.split("-"), "[1]", "");
  }

  return serviceNumber;
};

/**
 *
 * @param list any list
 * @param includeOriginalObject boolean includeOriginalObject
 * @returns return list array with extracted fields
 */
export const getListResult = (list: any, includeOriginalObject = false) => {
  const result =
    list &&
    list.map((v: any) => extractFields(v, false, includeOriginalObject));
  return result;
};

const extractDropdownOption = (item: any, keyNames: DropdownOptionKeys) => {
  const result: any = {};
  for (const [key, value] of Object.entries(keyNames)) {
    result[key] = item[value];
  }
  return result;
};

/**
 *
 * @param list array
 * @param keyNames dynamic key name {label: "key1", value: "key2"}
 * @returns array of dropdown options []{label: "label", value: "value"}
 */
export const getList = (list: any[], keyNames: DropdownOptionKeys) => {
  if (isArray(list)) {
    return list.map((v: any) => extractDropdownOption(v, keyNames));
  }
  return [];
};

/**
 *
 * @param patientId number patientId
 * @returns query string data
 */
export const queryStringData = (patientId: number) => {
  return {
    patientId: Number(patientId),
    contains: true,
    pageNo: DEFAULT_PAGE,
    pageSize: PER_PAGE,
    sortColumn: AUTH_ID,
    sortOrder: AUTHDESC,
  };
};

/**
 *
 * @param array T[] array
 * @param from number from
 * @param to number to
 * @returns array
 */
export function arrayMove<T>(array: T[], from: number, to: number) {
  array = array.slice();
  array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
  return array;
}

/**
 * allow only numbers or string as per regex filter
 * @param {any}  e - input field value typed in
 * @param {string} type - allowed filter type - onlyNumber or onlyText or onlyTextAndSpace
 *
 */
export const handleInputEvent = (e: any, type: string | undefined) => {
  const initialValue = e.target.value;
  const selectionStart = e.target.selectionStart;

  let newSelectionStart = selectionStart;

  switch (type) {
    case "onlyNumber":
      e.target.value = initialValue.replace(/\D*/g, "");
      break;
    case "onlyText":
      e.target.value = initialValue.replace(/[^a-zA-Z]/g, "");
      break;
    case "onlyTextAndSpace":
      e.target.value = initialValue.replace(/[^a-zA-Z\s]/g, "");
      break;
    case "onlyTextSpaceAndHyphen":
      e.target.value = initialValue.replace(alphabetSpaceAndHyphen, "");
      break;
    case "onlyNumAlphabetRegex":
      e.target.value = initialValue.replace(onlyNumAlphabetRegex, "");
      break;
  }
  if (initialValue !== e.target.value) {
    const removedCharsBeforeCursor = initialValue
      .substring(0, selectionStart)
      .split("")
      .filter((char: any) => !e.target.value.includes(char)).length;
    newSelectionStart = selectionStart - removedCharsBeforeCursor;
    e.target.setSelectionRange(newSelectionStart, newSelectionStart);
    e.stopPropagation();
  }
};

/**
 *
 * @param value string value
 *
 * @returns extracted doc type
 */
export const extractDocType = (value: string | null | undefined) => {
  if (!value || !value.includes("/")) {
    return DocumentType.INVALID;
  }
  const splitString: any = value.split("/")[1];
  const format: DocumentType = splitString;
  return format;
};

/**
 *
 * @param docName string docName
 *
 * @returns send fax doc is of below type and reutrn boolean value
 */
export const checkSendFaxDocType = (docName: string | null | undefined) => {
  const docType = extractDocType(docName);
  if (indexOf(["pdf", "jpg", "jpeg", "png"], docType) > -1) {
    return true;
  }
  return false;
};

/**
 *
 * @param type string type
 *
 * @returns gender
 */
export const SelectGender = (type: string) => {
  switch (type) {
    case "F":
      return Gender.FEMALE;
    case "M":
      return Gender.MALE;
    case "U":
      return Gender.UNKNOWN;
    default:
      return "";
  }
};

/**
 * To get the health plan and benifit plan data from base64 encoded string and separated by "|||"
 * @param {string}  base64String - base64 healthplan id and label string concatenated with "|||"
 * @param {number} index - 0 for healthplan id and 1 for health plan name
 *
 * @returns {string} healthplan id or health plan name
 */
export const getHealthPlanValueType = (
  base64String: string | undefined,
  index: number
): string => {
  return !isUndefined(base64String) && !isNill(base64String)
    ? atob(base64String).split("|||")[index]
    : "";
};

/**
 *
 * @param value any value
 *
 * @returns value or empty string
 */
export const isValid = (value: any) => {
  if (value.toString().toUpperCase().includes("INVALID")) {
    return "";
  }
  return value;
};

/**
 *
 * @param strings any strings
 *
 * @returns concatinated string
 */
export const getConcatStringOrNull = (...strings: any) => {
  return strings
    .filter((str: any) => str != null && str !== undefined)
    .join(" ");
};

/**
 *
 * @param errorResponse any errorResponse
 * @param setError UseFormSetError<any> setError
 *
 * @returns server side error message
 */
export const serverSideErrorMsg = (
  errorResponse: any,
  setError: UseFormSetError<any>
) => {
  return Object.keys(errorResponse).forEach((field) => {
    const messages = errorResponse[field];
    if (messages && isArray(messages)) {
      setError(field as any, {
        type: "server",
        message: messages.join(". "),
      });
    }
  });
};

/**phone number and extension*/
export const getPhoneNoAndExtension = (phoneNo: string, extension: string) => {
  if (!isEmpty(phoneNo)) {
    return phoneNo + `${!isEmpty(extension) ? " ext. " + extension : ""}`;
  }
  return "";
};

/**following function used to show error messages */
export const showErrorMessagesFromResponse = (response: any) => {
  Object.keys(response.data).forEach((field) => {
    return response.data[field].message;
  });
};

/**
 *
 * @param value string value
 *
 * @returns btoa value
 */
export const getBtoaValue = (value: string) => {
  return btoa(value);
};

/**
 *
 * @param value string value
 *
 * @returns getAtob value
 */
export const getAtob = (value: string) => {
  try {
    const decodedString = atob(value);
    const additionalValues = JSON.parse(decodedString);
    return additionalValues;
  } catch (e) {
    return {};
  }
};

/**My Patient Filter */
export const getPatientFilterPayload = (filterStatePayload: any) => {
  const newPayloadObject: any = {};
  for (const key in filterStatePayload) {
    if (
      typeof filterStatePayload[key] === "object" &&
      filterStatePayload[key] !== null &&
      "value" in filterStatePayload[key]
    ) {
      newPayloadObject[key] = filterStatePayload[key].value;
    } else {
      newPayloadObject[key] = filterStatePayload[key];
    }
  }
  return newPayloadObject;
};

/**
 *
 * @param singleOption ReceivedMethods singleOption
 * @param values ReAuthReferralRequest | PatientRequest value
 * @param key string key
 *
 * @returns boolean value
 */
export const fromProviderPortal = (
  singleOption: ReceivedMethods,
  values: ReAuthReferralRequest | PatientRequest | ReferralDiscipline,
  key: string
) => {
  if (singleOption) {
    return (
      singleOption.isIntegrationMethod &&
      getValue(values, key, null) == singleOption.receivedMethodId
    );
  }
  return false;
};

/**
 *
 * @param singleOption ReceivedMethods singleOption
 * @param patientHeaderData PatientInformationViewResponse
 *
 * @returns boolean value
 */
export const isManualReceivedMethods = (
  singleOption: ReceivedMethods,
  patientHeaderData: PatientInformationViewResponse
) => {
  if (singleOption && !patientHeaderData.loading) {
    return (
      singleOption.isIntegrationMethod &&
      patientHeaderData.response.isManual === BooleanValues.TRUE
    );
  }
  return false;
};

export const setLocalStorage = (key: string, data: any) => {
  return localStorage.setItem(key, JSON.stringify(data));
};

export const getLocalStorage = (key: string) => {
  const data = localStorage.getItem(key);
  if (data) {
    return JSON.parse(data);
  }
  return null;
};

/**Filter */
export const getCurrentTabName = (activeTab: number) => {
  switch (activeTab) {
    case 0:
      return "myPatient";
    case 1:
      return "allPending";
    case 2:
      return "incompleteRequest";
    case 3:
      return "staffingQueue";
    case 4:
      return "privateDutyAuth";
    default:
      return "";
  }
};

export const getFilterPayloadAsPerTab = (
  activeTab: number,
  payload: SearchFilterModel
) => {
  switch (activeTab) {
    case 0:
      return payload.myPatient;
    case 1:
      return payload.allPending;
    case 2:
      return payload.incompleteRequest;
    case 3:
      return payload.staffingQueue;
    case 4:
      return payload.privateDutyAuth;
    default:
      return payload.allPending;
  }
};

export const validInputTextLength = (
  inputTextLength: number,
  remainingChar: number
) => {
  return inputTextLength - remainingChar > 0;
};

export const remainingInputCharLength = (
  inputTextLength: number,
  remainingChar: number
) => {
  return inputTextLength - remainingChar;
};

export const messageForInputCharLength = (
  inputTextLength: number,
  remainingChar: number
) => {
  if (inputTextLength - remainingChar >= 0) {
    return `Remaining ${remainingInputCharLength(
      inputTextLength,
      remainingChar
    )} characters.`;
  }
  if (inputTextLength - remainingChar < 0) {
    return `Entered ${
      remainingInputCharLength(inputTextLength, remainingChar) * -1
    } characters more than expected ${inputTextLength} characters.`;
  }
};

export const isExpedited = (key: number | null) => {
  if (key && key == URGENCY_STATUS_EXPEDITED_ID) {
    return ExpeditedOptionColor;
  } else {
    return null;
  }
};

export const serviceStatusColor = (color: string) => {
  switch (color) {
    case ServiceStatus.PENDING:
      return others.otherColors[75];
    case ServiceStatus.EXPEDITED:
      return colors.red[100];
    case ServiceStatus.STANDARD:
      return colors.black[5];
    default:
      return colors.black[3];
  }
};

export const urgencyStatusColor = (color: string) => {
  switch (color) {
    case UrgencyStatus.EXPEDITED:
      return colors.red[100];
    case UrgencyStatus.STANDARD:
      return colors.black[5];
    default:
      return "";
  }
};

/**
 *
 * @param msg message string
 *
 * @returns boolean value which check to show or hide asterisk symbol for required text field
 */

export const checkValidationMsgSymbol = (msg: string) => {
  return !Object.values(requiredFieldMsg).includes(msg);
};

export const isEmptyValue = (obj: { [key: string]: any }): boolean => {
  return Object.values(obj).every(
    (value) => value === null || value === "" || value === undefined
  );
};

export const formatPhoneNumber = (phone: string | null) => {
  if (!isEmpty(phone)) {
    const cleanedPhone = phone && phone.replace(/\D/g, "");
    return cleanedPhone;
  } else {
    return null;
  }
};
/**
 * @param arrayObject DropdownOption[]
 * @param targetValue string
 * @param targetKey keyof DropdownOption
 *
 * @returns a boolean value after checking if the array of object consist of target value against the key
 */
export const isValuePresentInObjects = (
  arrayObject: DropdownOption[],
  targetValue: string,
  targetKey: keyof DropdownOption
) => {
  return arrayObject.some((obj) => obj[targetKey] === targetValue);
};

/**
 * @param num ofColumn[]
 *
 * @returns dynamic columns for level of function
 */
export const generateColumns = (num: number): LofColumn[] => {
  const base = "column";
  return Array.from({ length: num }, (_, index) => ({
    id: `${base}${index + 1}`,
    label: `Column ${index + 1}`,
  }));
};

/**
 * @param item any
 * @param keyPath string
 *
 * @returns dynamic row data for level of function
 */
export const getNoteEntryValue = (item: any, keyPath: string) => {
  if (!keyPath) return item;
  return keyPath.split(".").reduce((acc, part) => acc?.[part], item);
};

/* *
 * @param obj1 json object
 * @param obj2 json object
 *
 * @returns if both of the objects are identical this function will return true;
 */
export const areObjectsEqual = (obj1: any, obj2: any): boolean => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

/* *
 * @param arr any []
 *
 * @returns single object when array contains mixed entries some entries will be with key values and some with only text;
 */

export const convertToObject = (arr: any[]) => {
  const result: any = {};

  arr.forEach((item: any) => {
    let value, text;

    if (typeof item === "string") {
      value = item;
      text = item;
    } else {
      value = item.value;
      text = item.text;
    }

    result[value] = text;

    if (text !== value) {
      result[text] = text;
    }
  });

  return result;
};

/* *
 * @param pages any
 * @param elementName any
 *
 * @returns choices array which is present in elements of surveyjs json pages array when element name matches
 * this choices array includes text and values
 */

export const getChoicesForElement = (pages: any[], elementName: any) => {
  const getChoices = (element: any) => element.name === elementName ? element.choices || [] : null;

  for (const page of pages) {
    const element = page.elements.find((el: any) => {
      if (el.type === "panel") {
        const subElement = el.elements.find((sub: any) => sub.name === elementName);
        return subElement !== undefined;
      }
      return el.name === elementName;
    });

    if (element) {
      if (element.type === "panel") {
        const subElement = element.elements.find((sub: any) => sub.name === elementName);
        return subElement?.choices || [];
      }
      return getChoices(element) || [];
    }
  }

  return [];
};


/* *
 * @param pages any
 * @param elementName any
 *
 * @returns columns when element name matches
 * these columns includes entries array with text and values
 */

export const getColumnsForElement = (pages: any[], elementName: any) => {
  const getColumns = (element: any) => element.name === elementName ? element.columns || [] : null;

  for (const page of pages) {
    const element = page.elements.find((el: any) => {
      if (el.type === "panel") {
        return el.elements.some((sub: any) => sub.name === elementName);
      }
      return el.name === elementName;
    });

    if (element) {
      if (element.type === "panel") {
        const subElement = element.elements.find((sub: any) => sub.name === elementName);
        return subElement?.columns || [];
      }
      return getColumns(element) || [];
    }
  }

  return [];
};



/* *
 * @param data any
 *
 * @returns array with text and value pairs
 * this function used to normalize mixed array structure
 */

export const normalizeData = (data: any) => {
  return data.map((item: any) => {
    if (typeof item === "string") {
      return { value: item, text: item };
    }
    return item;
  });
};

/**
 * @param DefaultLOF any[]
 *
 * @returns LOF array's first object label which is present in DefaultLOF array
 */
export const generateLofRowKeys = (DefaultLOF: any[]) => {
  return DefaultLOF.map((item) => item.LOF[0]?.label).filter((label) => label);
};

export const getFormattedName = (str: string) => {
  return str
    .toLowerCase()
    .replace(/(^|\s|-)(\w)/g, (_, m1, m2) => m1 + m2.toUpperCase());
};

/**
 *
 *  @returns Array of objects of type State
 */

export const stateListData = (): NppesStateList[] => {
  return Object.entries(stateNameAbbreviations).map(([stateName, stateId]) => ({
    stateId,
    stateName,
  }));
};

/* *
 * @param state any
 * @param obj any
 *
 * @returns merged object of reducer state and object
 * this function used to merge state with object
 */

export const mergeObjects = (state: any, obj: any) => ({
  ...state,
  ...obj,
});

/* *
 * @param dob accepts Date of birth of patient
 * @param startDate  accepts start date of request
 * @param endDate    accepts end date of request
 *
 * @returns true if the patient is turning 21 in between
 * provided start and end date or is getting 21 before the start date
 */

export const is21OrTurning21Soon = (
  dob: string | Date,
  startDate: string,
  endDate: string
): boolean => {
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);
  const twentyFirstBirthday = dayjs(dob).add(21, "year");
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  return (
    (twentyFirstBirthday.isSameOrAfter(start) &&
      twentyFirstBirthday.isSameOrBefore(end)) ||
    twentyFirstBirthday.isSameOrBefore(start)
  );
};

/**
 * @param {string} jsons - input field value survey js json
 *
 * @returns surrvey js json with unique question names to differentiate different json structures
 */

let uniqJsonCount = 0;

export const makeQuesionNamesUnique = (json: any) => {
  for (const key in json) {
    if (key !== "pages") {
      const newKey = key + "_uniqJsonCount" + uniqJsonCount;
      json[newKey] = json[key];
      delete json[key];
    }
  }

  for (const element of json.pages[0].elements) {
    if (Object.prototype.hasOwnProperty.call(element, "elements")) {
      for (const subElement of element.elements) {
        subElement.name += "_uniqJsonCount" + uniqJsonCount;
      }
    } else {
      element.name += "_uniqJsonCount" + uniqJsonCount;
    }
  }
  uniqJsonCount++;
};

/**
 * allow only numbers or string as per regex filter
 * @param {any}  filename - input field value filename
 * @param {string} jsons - input field value survey js json
 * @param {string} surveyModels - input field value surveyModels for survey js template
 *
 * @returns combined pdf for survey js templates
 */

export const saveCombinedSurveyToPdf = (
  filename: string,
  jsons: NotesJson[],
  surveyModels: any[]
) => {
  const json = {
    pages: [] as any[],
  };
  for (const jsonItem of jsons) {
    json.pages.push(jsonItem.pages[0]);
  }
  const surveyPDF = new SurveyPDF(json);
  let data = {};
  for (const surveyModel of surveyModels) {
    data = Object.assign(data, surveyModel.jsonObj);
  }
  surveyPDF.data = data;
  surveyPDF.save(filename);
};

/**
 * Calculates the number of days pending from the received date to the current date.
 *
 * @param dateReceived - date when request was received
 * @returns The total number of days between the received date and today.
 *  If the received date is in the future, returns `0`.
 */
export const getDaysPending = (dateReceived: string) => {
  const receivedDate = dayjs(dateReceived);
  const currentDate = dayjs();
  return Math.max(0, currentDate.diff(receivedDate, "day"));
};

/**
 * Determines if urgencyStatus should be highlighted based on the urgency level and days pending.
 *
 * @param dateReceived - date when request was received
 * @param urgency - urgency level - "Standard", "Expedited"
 * @returns `true` if the urgencyStatus should be highlighted.
 */
export const isHighlighted = (
  daysPending: number,
  urgency: string,
  isBCBSTN?: boolean,
  authType?: string
) => {
  if (isBCBSTN) {
    if (urgency === PatientUrgencyStatus.EXPEDITED) {
      return true;
    }
    if (authType === AuthType.PRIOR_AUTH && daysPending >= 3) {
      return true;
    }
    if (authType === AuthType.RE_AUTH && daysPending >= 1) {
      return true;
    }
    return false;
  }
  return (
    (urgency === PatientUrgencyStatus.STANDARD && daysPending >= 4) ||
    (urgency === PatientUrgencyStatus.EXPEDITED && daysPending >= 2)
  );
};


/**
 * Determines if eligibility response sends language English or Spanish or Chinese or Arabic.
 *
 * @param checkEligibilityResponse - data when request was received
 * @returns `English or Spanish or Chinese or Arabic or undefined`
 */

export const getLanguage = (checkEligibilityResponse: any) => {
  const inputString = String(checkEligibilityResponse).toLowerCase();

  for (const [languageKey, keywords] of Object.entries(LANGUAGE_KEYWORDS)) {
    if (keywords.some(keyword => inputString.includes(keyword.toLowerCase()))) {
      return PatientLanguages[languageKey as keyof typeof PatientLanguages];
    }
  }

  return undefined;
};

/**
 * Unique benefitPlanName with respective benefitPlanIds.
 *
 * @param data - BenefitPlanDetails[]
 * @returns BenefitPlanGroup[]
 */
export const getUniqueBenefitPlans = (data: BenefitPlanDetails[]): BenefitPlanGroup[] => {
  const mapA = new Map();
  data.forEach(({ benefitPlanName, benefitPlanId }) => {
    if (!mapA.has(benefitPlanName)) {
      mapA.set(benefitPlanName, new Set());
    }
    mapA.get(benefitPlanName).add(benefitPlanId);
  });
  return Array.from(mapA.entries()).map(([name, ids]) => ({
    benefitPlanName: name,
    benefitPlanIds: Array.from(ids)
  }));
};

export const getNetworkStatus = (
  data: any,
  providerKey: string = "serviceProviderId",
  networkKey: string = "isOutOfNetwork"
): string => {
  if (!data || Object.keys(data).length === 0) return "";

  const hasProvider = getValue(data, providerKey, null);
  if (!hasProvider && providerKey === "serviceProviderId") return "";

  const isOutOfNetwork = getValue(data, networkKey, false);
  return isOutOfNetwork ? "OON" : "INN";
};

/**
 * Add 1s delay in synchronized test
 */
export const delay = () => {
  act(() => {
    jest.advanceTimersByTime(1000);
  });
};

/**
* Get medicareBeneficiaryIdentifier
* @param mbiNo any
* @returns Valid MBI or Empty string
*/
export const getMbiNo = (mbiNo: any): string => !isEmpty(mbiNo) && typeof mbiNo === 'string' && mbiRegex.test(mbiNo) ? mbiNo : "";

export const updateLastSyncDateInStorage = async (
  apiNameString: string,
  apiCallFn: (url: string, ...args: any[]) => Promise<any>,
  configUrl: string,
  ...args: any[]
) => {
  const storageData = checkLocalStorageData(apiNameString)
  if (storageData) {
    return storageData;
  }
  const key = PriorAuthMasterApiKey;
  const response = await apiCallFn(configUrl, ...args);
  const rawData = getLocalStorageData(key);
  if (!rawData && !isEqual(rawData , masterSyncPayload)) setLocalStorageData(PriorAuthMasterApiKey, masterSyncPayload);
  const payload: { apiName: string; lastSyncDate: string | null }[] = rawData;
  const currentDate = dayjs().utc();
  const updatedPayload = payload.map((item) =>
    item.apiName === ApiKeyNames[apiNameString as keyof typeof ApiKeyNames]
      ? { ...item, lastSyncDate: currentDate }
      : item
  );
  setLocalStorageData(key, updatedPayload);
  return response;
};

export const getLocalStorageData = (keyName: string) => {
  const data = localStorage.getItem(keyName);
  return !isNull(data) ? JSON.parse(data) : data;
};

export const setLocalStorageData = (keyName: string, data: any) => {
 const item =  JSON.stringify(data)
  return localStorage.setItem(keyName, item);
};

/**
 * @param keyName - key name in the localStorage
 * @returns data if available against the key in localStorage else returns false
 */
export const checkLocalStorageData = (keyName: string) => {
  const value = getLocalStorageData(keyName);
  // AB#33582: treat an empty cached list as "no data" so the master is refetched
  // instead of serving a blank dropdown from a stale/empty cache entry.
  const isEmptyList = Array.isArray(value) && value.length === 0;
  if (value !== null && value !== undefined && !isEmptyList) {
    const response =  { message: DataPresentInLocalStorage, data: value }
    return response;
  } else {
    return false;
  }
};
type ImportFactory<T extends ComponentType<any>> = () => Promise<{
  default: T;
}>;

export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: ImportFactory<T>
): LazyExoticComponent<T> {
  return React.lazy(() =>
    importFn().catch((error: unknown) => {
      if (
        error instanceof Error &&
        error.name === 'ChunkLoadError'
      ) {
        // Delete all service worker caches before reload
        if ('caches' in window) {
          caches.keys().then(function(names) {
            for (const name of names) {
              caches.delete(name);
            }
          }).finally(() => {
            window.location.reload();
          });
        } else {
          (window as Window).location.reload();
        }
      }

      throw error;
    })
  );
}

/**
 * Returns environment-specific Tempo configuration values for local development.
 */
export const GetEnvVariables = (): string => {
  let envConfigs = {} as any;

  if (IS_LOCALHOST) {
    const selectedEndpointEnv: string = REACT_APP_ENDPOINT_ENV;

    switch (selectedEndpointEnv) {
      case CloudEnvironmentConfig.SB1:
        envConfigs = localConfig?.[CloudEnvironmentConfig.SB1] ?? {};
        break;
      case CloudEnvironmentConfig.QA1:
        envConfigs = localConfig?.[CloudEnvironmentConfig.QA1] ?? {};
        break;
      case CloudEnvironmentConfig.DEV1:
        envConfigs = localConfig?.[CloudEnvironmentConfig.DEV1] ?? {};
        break;
      case CloudEnvironmentConfig.STG:
        envConfigs = localConfig?.[CloudEnvironmentConfig.STG] ?? {};
        break;
      case CloudEnvironmentConfig.SB2:
        envConfigs = localConfig?.[CloudEnvironmentConfig.SB2] ?? {};
        break;
      case CloudEnvironmentConfig.DEV2:
        envConfigs = localConfig?.[CloudEnvironmentConfig.DEV2] ?? {};
        break;
      case CloudEnvironmentConfig.QA2:
        envConfigs = localConfig?.[CloudEnvironmentConfig.QA2] ?? {};
        break;
    }
  }
  return envConfigs;
};
